import { FC, useEffect, useState } from 'react';

import { Box, Button, Grid, H6, Skeleton, Snackbar, TextField } from '@parspec/pixel';

import { REQUIRED_FIELD } from 'src/features/shared/constants';
import { useEditUserProfileInfoMutation, useGetUserProfileInfoQuery } from '../queries';
import { initialErrorState, trimEmptySpaces } from '../utils/utils';
import { useSnackbar } from 'src/features/shared/hooks/useSnackbar';

const AccountDetailsTab: FC = () => {
    const [profileState, setProfileState] = useState({ firstName: '', lastName: '', email: '' });
    const [errorState, setErrorState] = useState(initialErrorState);
    const { snackbarInfo, setSnackbarOpen, setSnackbarClose } = useSnackbar();

    const { data: userProfile, isLoading: userProfileLoading } = useGetUserProfileInfoQuery();

    const { mutateAsync: editUserDetailsMutation, isLoading: editUserDetailsLoading } = useEditUserProfileInfoMutation();

    useEffect(() => {
        if (!userProfile?.data) {
            return;
        }
        const profileData = { firstName: userProfile.data.first_name, lastName: userProfile.data.last_name, email: userProfile.data.email };
        setProfileState(() => profileData);
    }, [userProfile]);

    const onSubmitHandler = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (errorState.firstName.isError || errorState.lastName.isError) {
            return;
        }
        const trimmedFirstName = trimEmptySpaces(profileState.firstName);
        const trimmedLastName = trimEmptySpaces(profileState.lastName);
        const firstNameFlag = trimmedFirstName.length === 0;
        const lastNameFlag = trimmedLastName.length === 0;
        if (firstNameFlag || lastNameFlag) {
            return setErrorState({
                ...errorState,
                firstName: { ...errorState.firstName, isError: firstNameFlag, title: `${firstNameFlag ? REQUIRED_FIELD : ''}` },
                lastName: { ...errorState.lastName, isError: lastNameFlag, title: `${lastNameFlag ? REQUIRED_FIELD : ''}` }
            });
        }
        const payload = {
            first_name: trimmedFirstName,
            last_name: trimmedLastName,
            email: profileState.email
        };
        await editUserDetailsMutation(payload);
        setSnackbarOpen('User details updated successfully');
    };

    const resetDataHandler = () => {
        if (userProfile) {
            setProfileState(() => ({ firstName: userProfile.data.first_name, lastName: userProfile.data.last_name, email: userProfile.data.email }));
        }
        setErrorState(() => initialErrorState);
    };

    const keydownHandler = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onSubmitHandler(event as any);
        }
    };

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = event.target?.name;
        const fieldValue = event.target?.value;

        if (!Object.prototype.hasOwnProperty.call(errorState, fieldName) && !Object.prototype.hasOwnProperty.call(profileState, fieldName)) {
            return;
        }

        if (errorState[fieldName].isError) {
            setErrorState({
                ...errorState,
                [fieldName]: { ...errorState[fieldName], isError: false, title: '' }
            });
        }

        if (fieldName === 'firstName' || fieldName === 'lastName') {
            if (fieldValue.length === 0) {
                setErrorState({
                    ...errorState,
                    [fieldName]: { ...errorState[fieldName], isError: true, title: REQUIRED_FIELD }
                });
            }
        }

        setProfileState(() => ({ ...profileState, [fieldName]: fieldValue }));

    };

    const fieldBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const fieldName = event.target?.name;
        const eventType = event.type;

        if (!Object.prototype.hasOwnProperty.call(errorState, fieldName) && !Object.prototype.hasOwnProperty.call(profileState, fieldName)) {
            return;
        }

        if ((fieldName === 'firstName' || fieldName === 'lastName') && ((profileState?.[fieldName] || "").trim().length === 0 && eventType === 'blur')) {
            return setErrorState({ ...errorState, [fieldName]: { ...errorState[fieldName], isError: true, title: REQUIRED_FIELD } });
        }

        if (fieldName === 'firstName' || fieldName === 'lastName') {
            const trimmedValue = trimEmptySpaces(profileState[fieldName]);
            setProfileState(() => ({ ...profileState, [fieldName]: trimmedValue }));
        }
    };

    return (
        <>
            <Snackbar open={snackbarInfo.open} onClose={() => setSnackbarClose()} message={snackbarInfo.message} />
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <H6>Account Details</H6>

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box ml={2}>
                            <Button color="secondary" variant="outlined" onClick={resetDataHandler}>
                                Cancel
                            </Button>
                        </Box>
                        <Box ml={2}>
                            <Button onClick={onSubmitHandler} isLoading={editUserDetailsLoading}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Box>
                <Box mt={8}>
                    <form autoComplete={'off'}>
                        <Grid container spacing={6}>
                            <Grid item xs={6}>
                                <Grid item height={'72px'}>
                                    {!userProfileLoading ? (
                                        <TextField
                                            id={'firstName'}
                                            name={'firstName'}
                                            autoComplete={'given-name'}
                                            label={'First Name'}
                                            value={profileState?.firstName || ''}
                                            error={errorState?.firstName?.isError}
                                            helperText={errorState?.firstName?.title}
                                            onKeyDown={keydownHandler}
                                            onBlur={fieldBlurHandler}
                                            onChange={fieldChangeHandler}
                                            required
                                        />
                                    ) : (
                                        <Skeleton variant="rectangular" height={36} />
                                    )}
                                </Grid>

                                <Grid item height={'72px'}>
                                    {!userProfileLoading ? (
                                        <TextField
                                            id={'lastName'}
                                            name={'lastName'}
                                            autoComplete={'family-name'}
                                            label={'Last Name*'}
                                            value={profileState.lastName}
                                            error={errorState.lastName.isError}
                                            helperText={errorState.lastName.title}
                                            onKeyDown={keydownHandler}
                                            onBlur={fieldBlurHandler}
                                            onChange={fieldChangeHandler}
                                        />
                                    ) : (
                                        <Skeleton variant="rectangular" height={36} />
                                    )}
                                </Grid>
                                <Grid item height={'72px'}>
                                    {!userProfileLoading ? (
                                        <Box>
                                            <TextField id={'email'} name={'email'} autoComplete={'email'} label={'Email'} value={profileState.email} disabled />
                                        </Box>
                                    ) : (
                                        <Skeleton variant="rectangular" height={36} />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Box>
        </>
    );
};

export default AccountDetailsTab;
